import {useTranslation} from '@/translations/i18n'
import {AnswerField, Content, YesNoAnswerField} from '../atoms/Atoms'
import {forwardRef} from 'react'
import {Avatar} from '@/components/ui/avatar/Avatar'
import {Divider} from '@/components/ui/divider/Divider'
import {useGetCountries} from '@/features/experience-shell/queries/useGetCountries'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {RemapExperienceForViewOnly} from '@/features/experience-view-only/types'

export const BackgroundStep = forwardRef<HTMLDivElement, {experience: RemapExperienceForViewOnly}>(
    ({experience}, ref) => {
        const {t} = useTranslation()
        const countriesQuery = useGetCountries()
        const countryName = countriesQuery.data?.data.find(
            country => country.code == experience.expertise.answers.residential_country_region?.answer
        )?.name
        return (
            <Flexbox direction="column" fullWidth ref={ref}>
                <Content title={t('experience_shell:step_background')} margin={6}>
                    <Avatar
                        size={'lg'}
                        name={experience.host.firstName ?? '-'}
                        imageUrl={experience.host.profilePhotoUrl || ''}
                    />

                    <AnswerField
                        labelTitle={t('experience_shell:years_experience')}
                        hasValue={!!experience.expertise.answers.expertise_years?.answer}
                        value={experience.expertise.answers.expertise_years?.answer}
                    />

                    <AnswerField
                        labelTitle={t('experience_shell:online_profiles')}
                        hasValue={
                            !!experience.expertise.answers.online_profile_links?.answer &&
                            !!experience.expertise.answers.online_profile_links?.answer.length
                        }
                    >
                        {experience.expertise.answers.online_profile_links?.answer.map((item, index) => (
                            <li key={`${index}-${item}`} title={item}>
                                {item}
                            </li>
                        ))}
                    </AnswerField>

                    <AnswerField
                        labelTitle={t('experience_view_only:phone_number')}
                        hasValue={!!experience.host.airbnbVerifiedPhone}
                        value={
                            experience.host.airbnbVerifiedPhone ? `+${experience.host.airbnbVerifiedPhone}` : undefined
                        }
                    />
                </Content>

                <Divider direction="horizontal" />

                <Content margin={6}>
                    <AnswerField
                        labelTitle={t('experience_shell:country_region')}
                        hasValue={!!countryName}
                        value={countryName}
                    />
                    <AnswerField
                        labelTitle={t('experience_shell:street_address')}
                        hasValue={!!experience.expertise.answers.residential_address?.answer}
                        value={experience.expertise.answers.residential_address?.answer}
                    />
                    <AnswerField
                        labelTitle={t('experience_shell:apt_suite')}
                        hasValue={!!experience.expertise.answers.residential_address_extras?.answer}
                        value={experience.expertise.answers.residential_address_extras?.answer}
                    />

                    <AnswerField
                        labelTitle={t('experience_shell:state_territory')}
                        hasValue={!!experience.expertise.answers.residential_state?.answer}
                        value={experience.expertise.answers.residential_state?.answer}
                    />
                    <AnswerField
                        labelTitle={t('experience_shell:zip_code')}
                        hasValue={!!experience.expertise.answers.residential_zip?.answer}
                        value={experience.expertise.answers.residential_zip?.answer}
                    />
                </Content>

                <Divider direction="horizontal" />

                <Content margin={6}>
                    <YesNoAnswerField
                        labelTitle={t('experience_shell:hosting_as_business')}
                        yesNoValue={experience.expertise.answers?.hosting_as_business?.answer}
                    />
                </Content>
            </Flexbox>
        )
    }
)

BackgroundStep.displayName = 'BackgroundStep'
